import React, { useState, useEffect } from 'react';
import { 
  Send, 
  Plus, 
  Save, 
  Clock, 
  Upload, 
  Eye, 
  X, 
  Image as ImageIcon, 
  CheckCircle2 
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AdmissionsOnlineApplicationForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    summary: '',
    note: '',
    ctaLabel: '',
    ctaHref: '',
    image: '',
    highlights: []
  });

  const [pendingFields, setPendingFields] = useState({
    title: false,
    subtitle: false,
    summary: false,
    note: false,
    ctaLabel: false,
    ctaHref: false,
    image: false
  });

  const [pendingHighlights, setPendingHighlights] = useState(false);
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [tempHighlight, setTempHighlight] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(false);

  const getAuthHeaders = (extra = {}) => {
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...extra
    };
  };

  useEffect(() => {
    loadData();
    loadMediaMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/admissions/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: getAuthHeaders({ 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' })
        }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.ok && Array.isArray(result.data)) {
          const map = {};
          result.data.forEach(media => {
            const mediaId = media.id || media.media_id;
            const mediaUrl = media.url || media.file_path || media.filepath || '';
            if (mediaId && mediaUrl) {
              map[mediaId] = mediaUrl;
              map[String(mediaId)] = mediaUrl;
              const numId = Number(mediaId);
              if (!isNaN(numId)) map[numId] = mediaUrl;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (err) {
      console.warn('[AdmissionsOnlineApplicationForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    const str = String(mediaId).trim();
    if (!str) return null;

    // Reject video URLs for image preview
    if (str.endsWith('.mp4') || str.endsWith('.webm') || str.endsWith('.mov') || str.includes('.mp4')) {
      return null;
    }

    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:') || str.startsWith('blob:')) {
      return str;
    }

    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${str}` : str;
    }

    if (mediaMap[str]) return mediaMap[str];
    if (mediaMap[mediaId]) return mediaMap[mediaId];

    const resolved = resolveMediaSrc(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${resolved}` : resolved;
      }
      return resolved;
    }

    return str;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      let section = null;

      // 1. Fetch admissions sections via admin endpoint
      try {
        const response = await fetch(`${apiBase}/admissions/cms/sections`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const result = await response.json();
          if (result.ok && Array.isArray(result.data)) {
            section = result.data.find(s => s.section_type === 'admissions-online-application');
          }
        }
      } catch (err) {
        console.warn('[AdmissionsOnlineApplicationForm] Admin fetch warning:', err.message);
      }

      // 2. Fallback to public sections query if needed
      if (!section) {
        try {
          const pubRes = await fetch(`${apiBase}/admissions/cms/sections?published=true`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pubRes.ok) {
            const pubResult = await pubRes.json();
            if (pubResult.ok && Array.isArray(pubResult.data)) {
              section = pubResult.data.find(s => s.section_type === 'admissions-online-application');
            }
          }
        } catch (err) {
          console.warn('[AdmissionsOnlineApplicationForm] Public fetch warning:', err.message);
        }
      }

      if (section) {
        setSectionId(section.id);
        let content = section.content_published;
        if (typeof content === 'string') {
          try { content = JSON.parse(content); } catch (e) { content = {}; }
        }
        const en = content?.en || {};
        const onlineData = en.onlineApplication || {};

        const initialData = {
          title: en.title || onlineData.title || '',
          subtitle: en.subtitle || onlineData.subtitle || '',
          summary: en.summary || onlineData.summary || '',
          note: en.note || onlineData.note || '',
          ctaLabel: en.ctaLabel || en.cta_label || onlineData.ctaLabel || onlineData.cta_label || '',
          ctaHref: en.ctaHref || en.cta_href || onlineData.ctaHref || onlineData.cta_href || '',
          image: en.image || onlineData.image || '',
          highlights: Array.isArray(en.highlights) ? en.highlights : (Array.isArray(onlineData.highlights) ? onlineData.highlights : [])
        };

        const pMap = {
          title: false,
          subtitle: false,
          summary: false,
          note: false,
          ctaLabel: false,
          ctaHref: false,
          image: false
        };
        let hasPendingHighlights = false;

        // 3. Fetch pending changes
        try {
          const pRes = await fetch(`${apiBase}/cms/section/${section.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pRes.ok) {
            const pResult = await pRes.json();
            if (pResult.success && pResult.data?.pending_changes) {
              pResult.data.pending_changes.forEach(c => {
                if (c.status === 'pending' && (c.locale === 'en' || !c.locale)) {
                  let val = c.new_value;
                  if (typeof val === 'string') {
                    try { val = JSON.parse(val); } catch (e) {}
                  }

                  const fid = c.field_id || '';
                  if (fid in pMap) {
                    pMap[fid] = true;
                    if (val !== null && val !== undefined) {
                      initialData[fid] = val;
                    }
                  } else if (fid.startsWith('onlineApplication.')) {
                    const cleanField = fid.replace('onlineApplication.', '');
                    if (cleanField in pMap) {
                      pMap[cleanField] = true;
                      if (val !== null && val !== undefined) {
                        initialData[cleanField] = val;
                      }
                    }
                    if (cleanField === 'highlights' && Array.isArray(val)) {
                      hasPendingHighlights = true;
                      initialData.highlights = val;
                    }
                  } else if (fid === 'cta_label') {
                    pMap.ctaLabel = true;
                    if (val !== null && val !== undefined) initialData.ctaLabel = val;
                  } else if (fid === 'cta_href') {
                    pMap.ctaHref = true;
                    if (val !== null && val !== undefined) initialData.ctaHref = val;
                  } else if (fid.includes('highlights')) {
                    hasPendingHighlights = true;
                    if (Array.isArray(val)) {
                      initialData.highlights = val;
                    }
                  }
                }
              });
            }
          }
        } catch (pendingErr) {
          console.warn('[AdmissionsOnlineApplicationForm] Pending changes fetch warning:', pendingErr.message);
        }

        setFormData(initialData);
        setOriginalData(initialData);
        setPendingFields(pMap);
        setPendingHighlights(hasPendingHighlights);
      }
    } catch (err) {
      console.error('[AdmissionsOnlineApplicationForm] Load error:', err.message);
      toast.error('Failed to load online application configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);
      formDataToUpload.append('section_type', 'admissions-online-application');

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const res = JSON.parse(xhr.responseText);
            if (res.success || res.ok) {
              const mediaId = res.media_id || res.id || res.filename || res.url;
              const rawUrl = res.url || res.file_path || res.filepath || (res.filename ? `/api/uploads/admissions/${res.filename}` : '');
              const origin = (apiBase || '').replace(/\/api\/?$/, '');
              const fullUrl = rawUrl 
                ? (rawUrl.startsWith('http') ? rawUrl : `${origin}${rawUrl}`)
                : (mediaMap[mediaId] || String(mediaId));

              if (fullUrl) {
                setFormData(prev => ({ ...prev, image: fullUrl }));
                setImageLoadError(false);
                if (mediaId) {
                  setMediaMap(prev => ({
                    ...prev,
                    [mediaId]: fullUrl,
                    [String(mediaId)]: fullUrl
                  }));
                }
                toast.success('Graphic uploaded successfully! Click "Save & Submit Graphic" to submit for approval.');
              } else {
                toast.error('No media reference returned from server');
              }
            } else {
              toast.error(res.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/admissions/cms/media/upload?section_type=admissions-online-application`);
      xhr.withCredentials = true;
      const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleAddHighlight = () => {
    const trimmed = tempHighlight?.trim();
    if (!trimmed) return;
    if (formData.highlights.includes(trimmed)) {
      toast.warning('This benefit is already listed');
      return;
    }
    setFormData(prev => ({
      ...prev,
      highlights: [...prev.highlights, trimmed]
    }));
    setTempHighlight('');
  };

  const handleRemoveHighlight = (idx) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== idx)
    }));
  };

  const handleSubmitField = async (name) => {
    if (!sectionId) { 
      toast.error('Section ID not available'); 
      return; 
    }
    if (formData[name] === originalData[name]) { 
      toast.warning('No changes to submit'); 
      return; 
    }

    const fieldLabels = {
      subtitle: 'Section Tag / Subtitle',
      title: 'Banner Title',
      summary: 'Summary / Lead Text',
      ctaLabel: 'CTA Button Label',
      ctaHref: 'CTA Destination Route',
      note: 'Help / Guidance Note',
      image: 'Application Banner Graphic'
    };

    setSubmitting(p => ({ ...p, [name]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: name,
          field_name: fieldLabels[name] || name,
          old_value: originalData[name] || '',
          new_value: formData[name] || '',
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Submitted ${fieldLabels[name] || name} for Principal approval`);
        setPendingFields(p => ({ ...p, [name]: true }));
        setOriginalData(p => ({ ...p, [name]: formData[name] }));
      } else {
        toast.error(result.error || result.message || 'Submit failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [name]: false }));
    }
  };

  const handleSubmitHighlights = async () => {
    if (!sectionId) { 
      toast.error('Section ID not available'); 
      return; 
    }

    setSubmitting(p => ({ ...p, highlights: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: 'highlights.update',
          field_name: 'Online Application Highlights',
          old_value: originalData.highlights,
          new_value: formData.highlights,
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || result.ok) {
        toast.success('Application highlights submitted for Principal approval');
        setPendingHighlights(true);
        setOriginalData(p => ({ ...p, highlights: formData.highlights }));
      } else {
        toast.error(result.error || result.message || 'Submit failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, highlights: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading online application configuration...</p>
      </div>
    );
  }

  const resolvedImageUrl = getMediaUrl(formData.image);

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Send size={22} />
        </div>
        <div>
          <h2 className="unified-title">Online Application Call to Action</h2>
          <p className="unified-subtitle">
            Configure application banner prompts, CTA registration links, guidance notes, and banner graphics
          </p>
        </div>
      </div>

      {/* Section Subtitle Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Section Tag / Subtitle</label>
            <p className="unified-field-desc">Kicker label above the banner headline (e.g. Online Application)</p>
          </div>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.subtitle || ''}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={submitting.subtitle || pendingFields.subtitle}
            placeholder="e.g. Online Application"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle')}
            disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} />
            {submitting.subtitle ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Section Title Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Banner Title</label>
            <p className="unified-field-desc">Main callout title inviting prospective families to apply</p>
          </div>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title || ''}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. Online Application Form"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} />
            {submitting.title ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Summary Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Summary / Lead Text</label>
            <p className="unified-field-desc">Encouraging summary message explaining the easy online process</p>
          </div>
          {pendingFields.summary && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.summary || ''}
            onChange={(e) => handleFieldChange('summary', e.target.value)}
            disabled={submitting.summary || pendingFields.summary}
            placeholder="e.g. Ready to enroll? Start your application online in just a few minutes..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('summary')}
            disabled={submitting.summary || pendingFields.summary || formData.summary === originalData.summary}
          >
            <Save size={14} />
            {submitting.summary ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* CTA Label & Link Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Call-to-Action Button</label>
            <p className="unified-field-desc">The primary button label and destination route for the application form</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          {/* CTA Label */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>Button Label</label>
              {pendingFields.ctaLabel && (
                <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                  <Clock size={10} /> Pending
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.ctaLabel || ''}
                onChange={(e) => handleFieldChange('ctaLabel', e.target.value)}
                disabled={submitting.ctaLabel || pendingFields.ctaLabel}
                placeholder="e.g. Apply Online Now"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('ctaLabel')}
                disabled={submitting.ctaLabel || pendingFields.ctaLabel || formData.ctaLabel === originalData.ctaLabel}
                style={{ padding: '8px 16px', fontSize: '12px' }}
              >
                <Save size={13} /> Save
              </button>
            </div>
          </div>

          {/* CTA Href */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>Destination URL / Route</label>
              {pendingFields.ctaHref && (
                <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                  <Clock size={10} /> Pending
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.ctaHref || ''}
                onChange={(e) => handleFieldChange('ctaHref', e.target.value)}
                disabled={submitting.ctaHref || pendingFields.ctaHref}
                placeholder="e.g. /register or /admissions"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('ctaHref')}
                disabled={submitting.ctaHref || pendingFields.ctaHref || formData.ctaHref === originalData.ctaHref}
                style={{ padding: '8px 16px', fontSize: '12px' }}
              >
                <Save size={13} /> Save
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Advisory Note Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Help / Login Guidance Note</label>
            <p className="unified-field-desc">Notice providing contact instructions or login options for existing applicants</p>
          </div>
          {pendingFields.note && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.note || ''}
            onChange={(e) => handleFieldChange('note', e.target.value)}
            disabled={submitting.note || pendingFields.note}
            placeholder="e.g. Have questions? Contact admissions anytime. Already have an account? Log in to continue..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('note')}
            disabled={submitting.note || pendingFields.note || formData.note === originalData.note}
          >
            <Save size={14} />
            {submitting.note ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Banner Graphic Image Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Application Banner Graphic</label>
            <p className="unified-field-desc">Promotional image displayed alongside the online application prompt</p>
          </div>
          {pendingFields.image && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            padding: '20px',
            background: '#f8fafc',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            flexWrap: 'wrap'
          }}
        >
          {/* Image Preview */}
          <div
            style={{
              width: '260px',
              height: '160px',
              borderRadius: '10px',
              overflow: 'hidden',
              background: '#e2e8f0',
              flexShrink: 0,
              position: 'relative',
              border: '1px solid #cbd5e1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {resolvedImageUrl && !imageLoadError ? (
              <img
                src={resolvedImageUrl}
                alt="Online application graphic"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={() => setImageLoadError(true)}
              />
            ) : (
              <div style={{ textAlign: 'center', color: '#94a3b8', padding: '12px' }}>
                <ImageIcon size={32} style={{ margin: '0 auto 6px', opacity: 0.5 }} />
                <span style={{ fontSize: '12px', display: 'block' }}>
                  {formData.image && formData.image.includes('.mp4')
                    ? 'Video source detected (please upload an image file)'
                    : 'No valid image loaded'}
                </span>
              </div>
            )}
            {resolvedImageUrl && !imageLoadError && (
              <button
                type="button"
                onClick={() => setLightboxImage(resolvedImageUrl)}
                style={{
                  position: 'absolute',
                  bottom: '6px',
                  right: '6px',
                  background: 'rgba(0,0,0,0.65)',
                  color: '#fff',
                  border: 'none',
                  padding: '4px 7px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="View Full Graphic"
              >
                <Eye size={13} />
              </button>
            )}
          </div>

          {/* Upload & Actions */}
          <div style={{ flex: 1, minWidth: '240px' }}>
            <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
              Current Image Source:
              <br />
              <code style={{ fontSize: '11.5px', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', wordBreak: 'break-all' }}>
                {formData.image || 'None'}
              </code>
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type="file"
                id="online-app-graphic-input"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageUpload}
                disabled={imageUploading || pendingFields.image}
                style={{ display: 'none' }}
              />
              <label
                htmlFor="online-app-graphic-input"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  cursor: (imageUploading || pendingFields.image) ? 'not-allowed' : 'pointer',
                  color: '#2563eb',
                  fontWeight: 600,
                  fontSize: '13px'
                }}
              >
                <Upload size={14} />
                {imageUploading ? `Uploading (${imageProgress}%)...` : 'Upload New Graphic'}
              </label>

              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('image')}
                disabled={submitting.image || pendingFields.image || imageUploading || formData.image === originalData.image}
              >
                <Save size={14} />
                {submitting.image ? 'Submitting...' : imageUploading ? 'Uploading...' : 'Save & Submit Graphic'}
              </button>
            </div>
            {formData.image !== originalData.image && !imageUploading && (
              <div style={{ marginTop: '10px', padding: '6px 12px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '6px', fontSize: '12px', color: '#065f46', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle2 size={14} />
                <span>New graphic uploaded! Click "Save & Submit Graphic" to submit to Principal for approval.</span>
              </div>
            )}
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#94a3b8' }}>
              Max 5MB • Recommended: 800×450px • JPG, PNG, WebP
            </p>
          </div>
        </div>
      </div>

      {/* Highlights / Benefits Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Application Benefits & Highlights</label>
            <p className="unified-field-desc">Key highlights reassuring parents (e.g. 10-minute application, Secure upload)</p>
          </div>
          {pendingHighlights && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>

        {/* Add Highlight Row */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          <input
            type="text"
            className="unified-input"
            value={tempHighlight}
            onChange={(e) => setTempHighlight(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddHighlight())}
            placeholder="e.g. 10-minute application, Real-time status tracking..."
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={handleAddHighlight}
            style={{ padding: '8px 18px', fontSize: '12.5px' }}
          >
            <Plus size={14} /> Add Benefit
          </button>
        </div>

        {/* Highlights List */}
        {formData.highlights.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
            {formData.highlights.map((hl, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 14px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#1e293b'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle2 size={14} color="#16a34a" />
                  {hl}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveHighlight(idx)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '2px 4px'
                  }}
                  title="Remove highlight"
                >
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#94a3b8' }}>
            No highlights entered yet. Add quick benefits using the field above.
          </p>
        )}

        <button
          type="button"
          className="unified-submit-button"
          onClick={handleSubmitHighlights}
          disabled={submitting.highlights || pendingHighlights || JSON.stringify(formData.highlights) === JSON.stringify(originalData.highlights)}
        >
          <Save size={14} />
          {submitting.highlights ? 'Submitting...' : 'Save & Submit Highlights'}
        </button>
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              background: '#000',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10
              }}
            >
              <X size={18} />
            </button>
            <img
              src={lightboxImage}
              alt="Full Preview"
              style={{ maxWidth: '100%', maxHeight: '85vh', display: 'block', objectFit: 'contain' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
